import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertCircle, Info, Loader2, Settings2, SprayCan, Wrench } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useReservationsInWindow } from '@/hooks/useProcessSteps'
import {
  EquipmentToolbar,
  ALL_GROUPS,
  ALL_PURPOSES,
  ALL_MAINTAINED,
  maintainedFromFilter,
  type GroupFilter,
  type MaintainedFilter,
  type PurposeFilter,
} from '@/components/equipment/EquipmentToolbar'
import { EquipmentLegend } from '@/components/equipment/EquipmentLegend'
import { EquipmentGrid, useEquipmentGridSort } from '@/components/equipment/EquipmentGrid'
import { EquipmentTypeGrid, useEquipmentTypeGridSort } from '@/components/equipment/EquipmentTypeGrid'
import { PlannerPagination } from '@/components/equipment/PlannerPagination'
import { EquipmentInfoModal } from '@/components/equipment/EquipmentInfoModal'
import { EquipmentStorageTab } from '@/components/equipment/storage/EquipmentStorageTab'
import { PendingActionsTab } from '@/components/equipment/pending/PendingActionsTab'
import { LabelPreviewDialog, type LabelPreviewRequest } from '@/components/labels/LabelPreviewDialog'
import { equipmentLabelHtml, equipmentScanCode } from '@/lib/equipmentLabels'
import { EditEquipmentModal } from '@/components/equipment/EditEquipmentModal'
import { ConfigureScheduleModal, type ConfigureTarget } from '@/components/equipment/ConfigureScheduleModal'
import { CalendarEventsModal } from '@/components/equipment/CalendarEventsModal'
import { AddEquipmentModal, type NewEquipmentInput } from '@/components/equipment/AddEquipmentModal'
import { AddEquipmentTypeModal, type NewEquipmentTypeInput } from '@/components/equipment/AddEquipmentTypeModal'
import { EditEquipmentTypeModal } from '@/components/equipment/EditEquipmentTypeModal'
import { ManageGroupsModal } from '@/components/equipment/ManageGroupsModal'
import { ManageEventTypesModal } from '@/components/equipment/ManageEventTypesModal'
import { EquipmentCalendarTab } from '@/components/equipment/calendar/EquipmentCalendarTab'
import { MyListTab } from '@/components/equipment/mylist/MyListTab'
import { TeamScheduleTab, type ScheduleFocus } from '@/components/equipment/schedule/TeamScheduleTab'
import { SiteLayoutDialog } from '@/components/equipment/facility/SiteLayoutDialog'
import { OverallViewTab } from '@/components/equipment/facility/OverallViewTab'
import { BuildingSetupDialog } from '@/components/equipment/facility/BuildingSetupDialog'
import { cn } from '@/lib/utils'
import {
  EQUIPMENT_SCOPE,
  EquipmentScopeProvider,
  ROOM_SCOPE,
  useEquipmentScope,
  type EquipmentVariant,
} from '@/components/equipment/equipmentScope'
import { useEquipment, useEquipmentTypes } from '@/hooks/useEquipment'
import { useEventTypes, type EventTypeRow } from '@/hooks/useEventTypes'
import { filterEquipmentTypes, typeRepresentative } from '@/lib/equipment'
import type { Cycle, Equipment, EquipmentTypeRow } from '@/types/equipment'

type PlannerTab = 'equipment' | 'equipment-type' | 'storage' | 'my-list' | 'pending-actions' | 'calendar' | 'overall-view'
const PLANNER_TABS: readonly PlannerTab[] = ['equipment', 'equipment-type', 'storage', 'my-list', 'pending-actions', 'calendar', 'overall-view']

function LegendStrip({ eventTypes, onManage }: { eventTypes: EventTypeRow[]; onManage: () => void }) {
  return (
    <div className='flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-gray-100 bg-gray-50/60 px-4 py-2.5'>
      <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Event types</span>
      <EquipmentLegend eventTypes={eventTypes} />
      <button
        type='button'
        onClick={onManage}
        className='ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10'
      >
        <Settings2 className='h-3.5 w-3.5' />
        Manage event types
      </button>
    </div>
  )
}

function ScheduleScopeNote() {
  const { copy } = useEquipmentScope()
  return (
    <p className='mt-3 flex items-center gap-1.5 text-[11px] text-gray-400'>
      <Info className='h-3.5 w-3.5 shrink-0' />
      {copy.scheduleNote}
    </p>
  )
}

function LoadingRow() {
  const { copy } = useEquipmentScope()
  return (
    <div className='flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground'>
      <Loader2 className='h-4 w-4 animate-spin' />
      {copy.loading}
    </div>
  )
}

/**
 * The Equipment page, or — with `variant='room'` — the Cleaning page: the same
 * page listing only rooms and calling them rooms (Cleaning module, z8nrz7ewt3).
 * Same pattern as Kits on the Procedures page.
 */
export function EquipmentPlannerPage({ variant = 'equipment' }: { variant?: EquipmentVariant } = {}) {
  return (
    <EquipmentScopeProvider scope={variant === 'room' ? ROOM_SCOPE : EQUIPMENT_SCOPE}>
      <PlannerPage />
    </EquipmentScopeProvider>
  )
}

function PlannerPage() {
  const scope = useEquipmentScope()
  const { copy } = scope
  // Process-step holds for the next 30 days, so the Availability column can
  // say whether a unit is in use now or booked soon. Bookings occupy the same
  // machines as maintenance, so the equipment list must reflect them.
  const reservationWindow = useMemo(() => {
    const from = new Date()
    const to = new Date(from.getTime() + 30 * 86_400_000)
    return { from: from.toISOString(), to: to.toISOString() }
  }, [])
  const { data: liveReservations, error: reservationsError } = useReservationsInWindow(
    reservationWindow.from,
    reservationWindow.to,
    scope.purposes,
  )

  // The open tab IS `?tab=`: a link can land on one (a run's trail leads back
  // to Pending Actions, z8nrz7ewt1; a tenant switch to Storage), a refresh or a
  // copied link stays on it, and a navigation that changes it while the page
  // is open switches with it. Replaced, not pushed: switching tabs is not a
  // navigation to go Back through.
  const [searchParams, setSearchParams] = useSearchParams()
  const asked = searchParams.get('tab') as PlannerTab | null
  const tab: PlannerTab = asked && PLANNER_TABS.includes(asked) ? asked : 'equipment'
  const setTab = useCallback(
    (next: PlannerTab) =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set('tab', next)
          return params
        },
        { replace: true },
      ),
    [setSearchParams],
  )
  // `?task=<eventId>&day=<YYYY-MM-DD>` (My Tasks' Open, z8nrz7fh6h): land on
  // that task in the Team Schedule — its week, its dialog open. Cleared once
  // opened, so closing the dialog leaves an ordinary planner URL behind.
  const focusTask = searchParams.get('task')
  const scheduleFocus = useMemo<ScheduleFocus | null>(
    () => (focusTask ? { taskId: focusTask, day: searchParams.get('day') } : null),
    [focusTask, searchParams],
  )
  const clearScheduleFocus = useCallback(
    () =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.delete('task')
          params.delete('day')
          return params
        },
        { replace: true },
      ),
    [setSearchParams],
  )
  // Set when the Storage tab is opened from a unit's Information dialog, so
  // that tab lands on the unit the user was already looking at.
  const [storageFocusId, setStorageFocusId] = useState<string | null>(null)
  const [calendarView, setCalendarView] = useState<'team' | 'occurrences'>('team')
  // A task link always opens on the Team Schedule, where its dialog lives.
  useEffect(() => {
    if (focusTask) setCalendarView('team')
  }, [focusTask])
  const [search, setSearch] = useState('')
  const [group, setGroup] = useState<GroupFilter>(ALL_GROUPS)
  const [purpose, setPurpose] = useState<PurposeFilter>(ALL_PURPOSES)
  // Type-level only: a unit has no maintenance of its own, it inherits the
  // schedule of its type.
  const [maintained, setMaintained] = useState<MaintainedFilter>(ALL_MAINTAINED)
  const [sort, setSort] = useState('equipmentId:asc')

  // This page's purposes: the Equipment page lists every purpose but rooms; the
  // Cleaning page lists rooms and nothing else (z8nrz7ewt2).
  const { data, isLoading, error, groups, types, mutations } = useEquipment(scope.purposes)
  // Type names are unique across both pages, so the add/edit dialogs must also
  // refuse a name the other page holds — and say where it is.
  const { data: everyType } = useEquipmentTypes()
  const otherPageTypes = useMemo(
    () => (everyType ?? []).filter((t) => !scope.purposes.includes(t.purpose ?? 'PROCESSING')).map((t) => t.name),
    [everyType, scope.purposes],
  )
  const {
    eventTypes,
    error: eventTypesError,
    resolveMeta,
    addEventType,
    renameOrRecolor,
    removeEventType,
  } = useEventTypes()
  const baseRows = useMemo(() => data ?? [], [data])
  const groupNames = useMemo(() => groups.map((g) => g.name), [groups])

  const [infoEq, setInfoEq] = useState<Equipment | null>(null)
  // `?unit=<id>` (an Action Item's Source, z8nrz7fkfh): open that unit's
  // Information dialog. Cleared once read, like `?task=`.
  const focusUnit = searchParams.get('unit')
  useEffect(() => {
    if (!focusUnit || !data) return
    const unit = data.find((e) => e.id === focusUnit)
    if (unit) setInfoEq(unit)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.delete('unit')
        return params
      },
      { replace: true },
    )
  }, [focusUnit, data, setSearchParams])
  const [labelRequest, setLabelRequest] = useState<LabelPreviewRequest | null>(null)
  const [editEq, setEditEq] = useState<Equipment | null>(null)
  const [configTarget, setConfigTarget] = useState<ConfigureTarget | null>(null)
  const [calendarEq, setCalendarEq] = useState<Equipment | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [addTypeOpen, setAddTypeOpen] = useState(false)
  const [editingType, setEditingType] = useState<EquipmentTypeRow | null>(null)
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false)
  const [manageEventTypesOpen, setManageEventTypesOpen] = useState(false)
  // Cleaning only (z8nrz7f731): the site layout and its set-up.
  const [layoutOpen, setLayoutOpen] = useState(false)
  // The building picked on the Layout, for the Overall View to open on.
  const [overallFocus, setOverallFocus] = useState<{ buildingId: string; at: number } | null>(null)
  const [buildingSetupOpen, setBuildingSetupOpen] = useState(false)
  const siteLayoutControls =
    scope.variant === 'room'
      ? { onLayout: () => setLayoutOpen(true), onBuilding: () => setBuildingSetupOpen(true) }
      : {}
  const [saveError, setSaveError] = useState<string | null>(null)

  /**
   * Open the asset-tag preview for a unit.
   *
   * The symbol encodes `eq.id` — the immutable backend id — while the tag shows
   * `eq.equipmentId` (the editable "Equipment ID") as its heading, so renaming a
   * unit never invalidates a tag already on the hardware.
   */
  const showLabelFor = (eq: Equipment) => {
    const code = equipmentScanCode(eq.id)
    setLabelRequest({
      title: `Asset tag · ${eq.equipmentId}`,
      labels: [
        equipmentLabelHtml({
          role: copy.unit,
          id: eq.id,
          label: eq.equipmentId,
          typeName: eq.equipmentType,
          serial: eq.serial,
          // Already the mapped place where there is one, else the old text.
          place: eq.location.display === '—' ? null : eq.location.display,
          status: eq.status,
        }),
      ],
      codes: code ? [{ caption: copy.unit, value: code }] : [],
      print: { subject: 'equipment', ids: [eq.id] },
    })
  }

  const [pageSize, setPageSize] = useState(15)
  const [unitPage, setUnitPage] = useState(1)
  const [typePage, setTypePage] = useState(1)
  // Return to the first page whenever the result set changes.
  useEffect(() => {
    setUnitPage(1)
    setTypePage(1)
  }, [search, group, purpose, maintained, sort, pageSize])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return baseRows.filter((eq) => {
      // Group and purpose narrow together, deliberately: they are independent
      // axes, so "Cryopreservation + Storage" has to be askable.
      if (group !== ALL_GROUPS && eq.group !== group) return false
      if (purpose !== ALL_PURPOSES && eq.purpose !== purpose) return false
      // Third axis, same as the type view: a unit inherits its type's
      // maintenance, so "show me only the shelving" has to work on units too.
      const wantMaintained = maintainedFromFilter(maintained)
      if (wantMaintained !== null && eq.maintained !== wantMaintained) return false
      if (!q) return true
      return (
        eq.equipmentId.toLowerCase().includes(q) ||
        eq.equipmentType.toLowerCase().includes(q) ||
        eq.location.display.toLowerCase().includes(q)
      )
    })
  }, [baseRows, search, group, purpose, maintained])

  // From the type registry, not from the units: a type is created before any
  // unit of it exists, and collapsing units hid every one that had none.
  const typeRows = useMemo(
    () =>
      filterEquipmentTypes(types, {
        search,
        group: group === ALL_GROUPS ? null : group,
        purpose: purpose === ALL_PURPOSES ? null : purpose,
        maintained: maintainedFromFilter(maintained),
      }),
    [types, search, group, purpose, maintained],
  )
  const typeCount = typeRows.length

  // The calendar spans the whole fleet — independent of the grid toolbar filters.
  const allTypeRows = types

  const sortedUnits = useMemo(() => {
    const [field, dir] = sort.split(':')
    const key = (eq: Equipment) =>
      field === 'group'
        ? eq.group
        : field === 'equipmentType'
          ? eq.equipmentType
          : field === 'location'
            ? eq.location.display
            : eq.equipmentId
    const factor = dir === 'desc' ? -1 : 1
    return [...filtered].sort(
      (a, b) => factor * key(a).localeCompare(key(b), undefined, { numeric: true }),
    )
  }, [filtered, sort])

  const sortedTypes = useMemo(() => {
    const [field, dir] = sort.split(':')
    const key = (r: (typeof typeRows)[number]) => (field === 'group' ? r.group : r.equipmentType)
    const factor = dir === 'desc' ? -1 : 1
    return [...typeRows].sort(
      (a, b) => factor * key(a).localeCompare(key(b), undefined, { numeric: true }),
    )
  }, [typeRows, sort])

  // Column-header sort (z8nrz7e4h6), applied to the WHOLE list before paging —
  // sorting inside the grid alone would order only the rows on this page. It
  // sits on top of the toolbar's sort; clearing it returns to that order.
  const holds = useMemo(() => liveReservations ?? [], [liveReservations])
  const unitHeader = useEquipmentGridSort(sortedUnits, holds, !!reservationsError)
  const typeHeader = useEquipmentTypeGridSort(sortedTypes)
  // A new header sort reorders the whole list, so start again from page 1.
  useEffect(() => setUnitPage(1), [unitHeader.sortKey])
  useEffect(() => setTypePage(1), [typeHeader.sortKey])

  const unitPages = Math.max(1, Math.ceil(unitHeader.rows.length / pageSize))
  const pagedUnits = unitHeader.rows.slice(
    (Math.min(unitPage, unitPages) - 1) * pageSize,
    Math.min(unitPage, unitPages) * pageSize,
  )

  const typePages = Math.max(1, Math.ceil(typeHeader.rows.length / pageSize))
  const pagedTypes = typeHeader.rows.slice(
    (Math.min(typePage, typePages) - 1) * pageSize,
    Math.min(typePage, typePages) * pageSize,
  )

  const run = (op: Promise<void>) => {
    setSaveError(null)
    op.catch((err: unknown) => {
      setSaveError(err instanceof Error ? err.message : 'Saving failed')
    })
  }

  const saveSchedule = (
    cycle: Cycle,
    eventTypes: Parameters<typeof mutations.saveSchedule>[2],
    note?: string,
    weeklyDays?: Parameters<typeof mutations.saveSchedule>[4],
    preferredTiming?: Parameters<typeof mutations.saveSchedule>[5],
    startTime?: Parameters<typeof mutations.saveSchedule>[6],
    endTime?: Parameters<typeof mutations.saveSchedule>[7],
    procedures?: Parameters<typeof mutations.saveSchedule>[8],
  ) => {
    if (!configTarget) return
    run(
      mutations.saveSchedule(
        configTarget.equipment.equipmentType,
        cycle,
        eventTypes,
        note,
        weeklyDays,
        preferredTiming,
        startTime,
        endTime,
        procedures,
      ),
    )
  }

  const saveCalendar = (events: Parameters<typeof mutations.saveCalendar>[1]) => {
    if (!calendarEq) return
    run(mutations.saveCalendar(calendarEq.equipmentType, events))
  }

  const addEquipment = (input: NewEquipmentInput) => {
    run(
      mutations
        .addEquipment({
          label: input.equipmentId,
          equipmentType: input.equipmentType,
          group: input.group,
          model: input.model || undefined,
          serial: input.serial || undefined,
          vendor: input.vendor || undefined,
          installed: input.installed,
          roomId: input.roomId,
          floorId: input.floorId,
        })
        .then((id) => {
          // Offer the tag at the one moment someone is holding the machine and
          // has a printer open. Skipped in mock mode, where there is no id to
          // encode — a tag that resolves to nothing is worse than no tag.
          if (!id) return
          setLabelRequest({
            title: `Asset tag · ${input.equipmentId}`,
            labels: [
              equipmentLabelHtml({
                role: copy.unit,
                id,
                label: input.equipmentId,
                typeName: input.equipmentType,
                serial: input.serial || null,
                place: input.place,
              }),
            ],
            codes: [{ caption: copy.unit, value: equipmentScanCode(id) as string }],
            print: { subject: 'equipment', ids: [id] },
          })
        }),
    )
  }

  const addEquipmentType = (input: NewEquipmentTypeInput) => {
    run(
      mutations.addEquipmentType({
        name: input.equipmentType,
        group: input.group,
        purpose: input.purpose,
        maintained: input.maintained,
        preStartChecklist: input.preStartChecklist,
        defaultAssigneeId: input.defaultAssigneeId,
        ...(input.cleanHoldTimeHours !== undefined ? { cleanHoldTimeHours: input.cleanHoldTimeHours } : {}),
      }),
    )
  }

  const updateEquipmentType = (
    id: string,
    input: {
      name: string
      group: Equipment['group']
      purpose: Equipment['purpose']
      maintained: boolean
      preStartChecklist: { label: string; required: boolean }[]
      defaultAssigneeId: string
      cleanHoldTimeHours?: number | null
    },
  ) => {
    run(
      mutations.updateEquipmentType({
        id,
        name: input.name,
        group: input.group,
        // The edit modal offers Purpose, so it has to survive the save. Dropping
        // it here is what made the control look editable and read back unchanged
        // after a refresh.
        purpose: input.purpose,
        maintained: input.maintained,
        preStartChecklist: input.preStartChecklist,
        defaultAssigneeId: input.defaultAssigneeId,
        ...(input.cleanHoldTimeHours !== undefined ? { cleanHoldTimeHours: input.cleanHoldTimeHours } : {}),
      }),
    )
  }

  // Type-view rows use a representative unit (schedules are shared across the
  // type), falling back to a stand-in so a type with no units yet is still
  // configurable rather than a button that does nothing.
  const representativeFor = (equipmentType: string): Equipment | null => {
    const row = types.find((t) => t.equipmentType === equipmentType)
    return row ? typeRepresentative(row, baseRows) : null
  }
  const configureType = (equipmentType: string, cycle: Cycle) => {
    const rep = representativeFor(equipmentType)
    if (rep) setConfigTarget({ equipment: rep, cycle, fromType: true })
  }
  const calendarForType = (equipmentType: string) => {
    const rep = representativeFor(equipmentType)
    if (rep) setCalendarEq(rep)
  }

  // A failed reservation read falls back to an empty list, which renders every
  // unit as free — the one wrong answer that matters here, since it invites
  // booking a machine that is already taken. Say so rather than showing it.
  const loadError =
    error ??
    eventTypesError ??
    (reservationsError
      ? `${copy.unit} availability could not be loaded — booked units may show as available.`
      : null)
  const banner = saveError ?? loadError

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='w-full px-6 py-8'>
        <div className='mb-5 flex items-center gap-3'>
          <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary'>
            {scope.variant === 'room' ? (
              <SprayCan className='h-5 w-5 text-primary-foreground' />
            ) : (
              <Wrench className='h-5 w-5 text-primary-foreground' />
            )}
          </div>
          <h1 className='text-2xl font-semibold tracking-tight text-gray-900'>{copy.page}</h1>
        </div>

        {banner && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {banner}
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as PlannerTab)}>
          <TabsList>
            <TabsTrigger value='equipment'>{copy.unit}</TabsTrigger>
            <TabsTrigger value='equipment-type'>{copy.type}</TabsTrigger>
            {/* Rooms never hold stock, so the Cleaning page has no Storage tab. */}
            {scope.showStorage && <TabsTrigger value='storage'>Storage</TabsTrigger>}
            <TabsTrigger value='my-list'>Tasks</TabsTrigger>
            <TabsTrigger value='pending-actions'>Pending Actions</TabsTrigger>
            <TabsTrigger value='calendar'>Calendar</TabsTrigger>
            {/* Cleaning only: the site drawn room by room (z8nrz7f731). */}
            {scope.variant === 'room' && <TabsTrigger value='overall-view'>Overall View</TabsTrigger>}
          </TabsList>

          {scope.variant === 'room' && (
            <TabsContent value='overall-view' className='mt-4'>
              <OverallViewTab
                onOpenTasks={() => setTab('my-list')}
                onSetUp={() => setBuildingSetupOpen(true)}
                onOpenLayout={() => setLayoutOpen(true)}
                focus={overallFocus}
              />
            </TabsContent>
          )}

          <TabsContent value='my-list' className='mt-4'>
            <MyListTab />
          </TabsContent>

          {/* Tasks whose cycle carries a procedure, until the task ends (z8nrz7ewt1). */}
          <TabsContent value='pending-actions' className='mt-4'>
            <PendingActionsTab />
          </TabsContent>

          {/* Layout configuration for Purpose=Storage units. Browsing what is
              stored stays under Inventory → Storage. */}
          {scope.showStorage && (
            <TabsContent value='storage' className='mt-4'>
              <EquipmentStorageTab focusEquipmentId={storageFocusId} />
            </TabsContent>
          )}

          <TabsContent value='equipment' className='mt-4'>
            <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='border-b border-gray-100 p-4'>
                <EquipmentToolbar
                  search={search}
                  onSearchChange={setSearch}
                  group={group}
                  onGroupChange={setGroup}
                  groups={groupNames}
                  purpose={purpose}
                  onPurposeChange={setPurpose}
                  maintained={maintained}
                  onMaintainedChange={setMaintained}
                  onManageGroups={() => setManageGroupsOpen(true)}
                  sort={sort}
                  onSortChange={setSort}
                  onAdd={() => setAddOpen(true)}
                  addLabel={copy.addUnit}
                  {...siteLayoutControls}
                />
              </div>
              <LegendStrip eventTypes={eventTypes} onManage={() => setManageEventTypesOpen(true)} />
              <div className='p-4'>
                {isLoading ? (
                  <LoadingRow />
                ) : (
                  <EquipmentGrid
                    rows={pagedUnits}
                    resolveMeta={resolveMeta}
                    onInfo={setInfoEq}
                    onConfigure={(eq, cycle) => setConfigTarget({ equipment: eq, cycle })}
                    onCalendar={setCalendarEq}
                    reservations={liveReservations ?? []}
                    reservationsUnavailable={!!reservationsError}
                  />
                )}
                <PlannerPagination
                  page={unitPage}
                  pageSize={pageSize}
                  total={filtered.length}
                  summarySuffix={`· ${typeCount} types`}
                  onPageChange={setUnitPage}
                  onPageSizeChange={setPageSize}
                />
                <ScheduleScopeNote />
              </div>
            </div>
          </TabsContent>

          <TabsContent value='equipment-type' className='mt-4'>
            <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='border-b border-gray-100 p-4'>
                <EquipmentToolbar
                  search={search}
                  onSearchChange={setSearch}
                  group={group}
                  onGroupChange={setGroup}
                  groups={groupNames}
                  purpose={purpose}
                  onPurposeChange={setPurpose}
                  maintained={maintained}
                  onMaintainedChange={setMaintained}
                  onManageGroups={() => setManageGroupsOpen(true)}
                  sort={sort}
                  onSortChange={setSort}
                  onAdd={() => setAddTypeOpen(true)}
                  addLabel={copy.addType}
                  {...siteLayoutControls}
                />
              </div>
              <LegendStrip eventTypes={eventTypes} onManage={() => setManageEventTypesOpen(true)} />
              <div className='p-4'>
                {isLoading ? (
                  <LoadingRow />
                ) : (
                  <EquipmentTypeGrid
                    rows={pagedTypes}
                    resolveMeta={resolveMeta}
                    onConfigure={configureType}
                    onCalendar={calendarForType}
                    onEdit={setEditingType}
                  />
                )}
                <PlannerPagination
                  page={typePage}
                  pageSize={pageSize}
                  total={typeRows.length}
                  summarySuffix={`· ${filtered.length} ${copy.unitMany}`}
                  onPageChange={setTypePage}
                  onPageSizeChange={setPageSize}
                />
                <ScheduleScopeNote />
              </div>
            </div>
          </TabsContent>

          <TabsContent value='calendar' className='mt-4'>
            <div className='mb-3 flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5 w-fit'>
              {(['team', 'occurrences'] as const).map((v) => (
                <button
                  key={v}
                  type='button'
                  onClick={() => setCalendarView(v)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    calendarView === v
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-gray-600 hover:bg-white hover:text-gray-900',
                  )}
                >
                  {v === 'team' ? 'Team Schedule' : 'Occurrences'}
                </button>
              ))}
            </div>
            {calendarView === 'team' ? (
              <TeamScheduleTab focus={scheduleFocus} onFocused={clearScheduleFocus} />
            ) : (
              <EquipmentCalendarTab
                types={allTypeRows}
                eventTypes={eventTypes}
                resolveMeta={resolveMeta}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <LabelPreviewDialog request={labelRequest} onClose={() => setLabelRequest(null)} />
      <EquipmentInfoModal
        equipment={infoEq}
        onClose={() => setInfoEq(null)}
        onEdit={(eq) => {
          setInfoEq(null)
          setEditEq(eq)
        }}
        onPrintLabel={(eq) => {
          // Close Info first: two stacked dialogs trap focus in the wrong one.
          setInfoEq(null)
          showLabelFor(eq)
        }}
        onConfigureStorage={(eq) => {
          setInfoEq(null)
          setStorageFocusId(eq.id)
          setTab('storage')
        }}
      />
      <EditEquipmentModal
        equipment={editEq}
        onClose={() => setEditEq(null)}
        onSave={(id, input) => run(mutations.editEquipment(id, input))}
      />
      <ConfigureScheduleModal
        target={configTarget}
        eventTypes={eventTypes}
        onClose={() => setConfigTarget(null)}
        onSave={saveSchedule}
      />
      <CalendarEventsModal
        target={calendarEq}
        onClose={() => setCalendarEq(null)}
        onSave={saveCalendar}
      />
      <AddEquipmentModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={addEquipment}
        types={allTypeRows.map((r) => ({
          name: r.equipmentType,
          group: r.group,
          maintained: r.maintained,
        }))}
      />
      <AddEquipmentTypeModal
        open={addTypeOpen}
        onClose={() => setAddTypeOpen(false)}
        onAdd={addEquipmentType}
        existingTypes={allTypeRows.map((r) => r.equipmentType)}
        groups={groupNames}
        takenElsewhere={otherPageTypes}
      />
      <EditEquipmentTypeModal
        open={editingType !== null}
        onClose={() => setEditingType(null)}
        type={
          editingType && editingType.id
            ? {
                id: editingType.id,
                name: editingType.equipmentType,
                group: editingType.group,
                purpose: editingType.purpose,
                maintained: editingType.maintained,
                // Carried so the dialog can say what switching maintenance off
                // would stop, with the count, before it is saved.
                schedule: editingType.schedule,
                calendar: editingType.calendar,
                preStartChecklist: editingType.preStartChecklist ?? [],
                defaultAssigneeId: editingType.defaultAssigneeId,
                cleanHoldTimeHours: editingType.cleanHoldTimeHours ?? null,
              }
            : null
        }
        existingTypes={allTypeRows
          .map((r) => r.equipmentType)
          .filter((name) => name !== editingType?.equipmentType)}
        groups={groupNames}
        takenElsewhere={otherPageTypes}
        onSave={updateEquipmentType}
      />
      <ManageGroupsModal
        open={manageGroupsOpen}
        onClose={() => setManageGroupsOpen(false)}
        groups={groups}
        onAdd={mutations.addGroup}
        onRename={mutations.renameGroup}
        onRemove={mutations.removeGroup}
      />
      {scope.variant === 'room' && (
        <>
          <SiteLayoutDialog
            open={layoutOpen}
            onOpenChange={setLayoutOpen}
            onSetUp={() => {
              setLayoutOpen(false)
              setBuildingSetupOpen(true)
            }}
            onOpenBuilding={(buildingId) => {
              setLayoutOpen(false)
              setOverallFocus({ buildingId, at: Date.now() })
              setTab('overall-view')
            }}
          />
          <BuildingSetupDialog open={buildingSetupOpen} onOpenChange={setBuildingSetupOpen} />
        </>
      )}
      <ManageEventTypesModal
        open={manageEventTypesOpen}
        onClose={() => setManageEventTypesOpen(false)}
        eventTypes={eventTypes}
        onAdd={addEventType}
        onEdit={renameOrRecolor}
        onRemove={removeEventType}
      />
    </div>
  )
}
